// ── Billing State Machine ─────────────────────────────────────────────────────
//
// This is the SINGLE source of truth for all billing entitlement decisions.
// Every UI gate and permission check MUST call getBillingState() — never read
// plan/status/activated_at/downgrade_pending directly in components or hooks.
//
// State lifecycle:
//   FREE → PENDING_ACTIVATION → PRO_ACTIVE → PRO_GRACE → FREE

export type BillingState =
  | 'FREE'
  | 'PENDING_ACTIVATION'
  | 'PRO_ACTIVE'
  | 'PRO_GRACE'
  | 'CANCELLED';

export interface BillingSubscription {
  id: string;
  user_id: string;
  paypal_subscription_id: string | null;
  paypal_plan_id: string | null;
  status: 'ACTIVE' | 'CANCELLED' | 'EXPIRED' | 'SUSPENDED' | 'PENDING';
  plan: 'free' | 'pro';
  seats_included: number;
  seats_extra: number;
  plan_expires_at: string | null;
  downgrade_pending: boolean;
  billing_cycle: 'monthly' | 'yearly' | null;
  activated_at: string | null;
}

export function getBillingState(sub: BillingSubscription | null): BillingState {
  if (!sub) return 'FREE';

  const isActivated = sub.activated_at != null;
  const isPro       = sub.plan === 'pro';
  const isActive    = sub.status === 'ACTIVE';

  // PayPal subscription created but webhook hasn't confirmed activation yet
  if (isPro && isActive && !isActivated) return 'PENDING_ACTIVATION';

  // Fully active Pro — webhook confirmed, no cancellation pending
  if (isPro && isActive && isActivated && !sub.downgrade_pending) return 'PRO_ACTIVE';

  // Cancelled/suspended but still within the paid billing period
  if (
    isPro &&
    sub.downgrade_pending &&
    sub.plan_expires_at &&
    new Date(sub.plan_expires_at) > new Date()
  ) return 'PRO_GRACE';

  // Explicitly cancelled with no remaining access
  if (sub.status === 'CANCELLED' || sub.status === 'EXPIRED' || sub.status === 'SUSPENDED') {
    return 'CANCELLED';
  }

  return 'FREE';
}

// Convenience predicates — use these in UI conditions rather than comparing
// the string literals directly, to make call sites read clearly.
export const isProActive  = (sub: BillingSubscription | null) => getBillingState(sub) === 'PRO_ACTIVE';
export const isProOrGrace = (sub: BillingSubscription | null) => {
  const s = getBillingState(sub);
  return s === 'PRO_ACTIVE' || s === 'PRO_GRACE';
};
export const isPending    = (sub: BillingSubscription | null) => getBillingState(sub) === 'PENDING_ACTIVATION';
